import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';
import { getAdminMedia, createAdminMedia, updateAdminMedia, deleteAdminMedia, toggleMediaFeatured } from '@/services/admin/media.admin.api';
import { FrontendMedia } from '@/services/transform';

const BLANK = {
  source: '', 
  sourceLogoColor: '#374151', 
  title: '', 
  titleEn: '',
  excerpt: '', 
  excerptEn: '', 
  date: new Date().toISOString().split('T')[0],
  category: '', 
  categoryEn: '', 
  url: '#', 
  featured: false,
};

export default function MediaPage() {
  const { t } = useTranslation();
  const { can } = useRBAC();
  const canView = can('media:view');
  const canWrite = can('media:write');
  const canDelete = can('media:delete');
  const [items, setItems] = useState<FrontendMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFeatured, setFilterFeatured] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<FrontendMedia | null>(null);
  const [form, setForm] = useState<any>(BLANK);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getAdminMedia();
      setItems(data || []);
    } catch (error) {
      console.error('加载媒体报道失败:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return (items || []).filter((m) => {
      if (filterFeatured === 'featured' && !m.featured) return false;
      if (filterFeatured === 'normal' && m.featured) return false;
      if (search && !m.title.includes(search) && !m.source.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [items, search, filterFeatured]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...BLANK, date: new Date().toISOString().split('T')[0] });
    setShowModal(true);
  };

  const openEdit = (m: FrontendMedia) => {
    setEditing(m);
    setForm({ ...m });
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const itemData = {
        source: form.source || '',
        sourceLogoColor: form.sourceLogoColor || '#374151',
        title: form.title || '',
        titleEn: form.titleEn || '',
        excerpt: form.excerpt || '',
        excerptEn: form.excerptEn || '',
        date: form.date || new Date().toISOString().split('T')[0],
        category: form.category || '',
        categoryEn: form.categoryEn || '',
        url: form.url || '#',
        featured: !!form.featured,
      };

      if (editing) {
        await updateAdminMedia(editing.id, itemData);
        showToast('媒体报道已更新');
      } else {
        await createAdminMedia(itemData);
        showToast('媒体报道已添加');
      }
      await loadData();
      setShowModal(false);
    } catch (error) {
      console.error('保存媒体报道失败:', error);
      showToast('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAdminMedia(id);
      await loadData();
      setDeleteConfirm(null);
      showToast('已删除');
    } catch (error) {
      console.error('删除媒体报道失败:', error);
      showToast('删除失败，请重试');
    }
  };

  const toggleFeatured = async (id: string) => {
    try {
      await toggleMediaFeatured(id);
      await loadData();
    } catch (error) {
      console.error('切换精选状态失败:', error);
      showToast('操作失败，请重试');
    }
  };

  const f = (key: keyof MediaItem, val: string | boolean) => setForm((prev) => ({ ...prev, [key]: val }));

  if (!canView) return <NoPermission resource="媒体报道" />;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl shadow-lg">{toast}</div>}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">媒体报道管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {items.length} 篇报道，{items.filter((m) => m.featured).length} 篇置顶</p>
        </div>
        {canWrite && (
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#18181B] text-white text-sm font-medium rounded-lg hover:bg-[#27272A] cursor-pointer whitespace-nowrap transition-colors">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>添加报道
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
            <i className="ri-search-line text-[#9CA3AF] text-sm" />
          </div>
          <input type="text" placeholder="搜索标题/来源..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none bg-white w-52" />
        </div>
        <select value={filterFeatured} onChange={(e) => setFilterFeatured(e.target.value)} className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部</option>
          <option value="featured">置顶报道</option>
          <option value="normal">普通报道</option>
        </select>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] py-12 text-center text-[#9CA3AF] text-sm">暂无数据</div>
        ) : filtered.map((m) => (
          <div key={m.id} className="bg-white rounded-xl border border-[#E5E7EB] p-4 hover:border-[#D1D5DB] transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: m.sourceLogoColor }}>
                {m.source.slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {m.featured && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B] font-medium">置顶</span>
                      )}
                      <span className="text-xs text-[#9CA3AF]">{m.source}</span>
                      <span className="text-[10px] text-[#9CA3AF]">{m.date}</span>
                    </div>
                    <div className="font-medium text-[#18181B] text-sm line-clamp-1">{m.title}</div>
                    <div className="text-xs text-[#9CA3AF] line-clamp-1 mt-0.5">{m.excerpt}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canWrite && <button onClick={() => toggleFeatured(m.id)} className={`px-2 py-1 text-[10px] font-medium rounded cursor-pointer whitespace-nowrap transition-colors ${m.featured ? 'text-[#F59E0B] bg-amber-50 hover:bg-amber-100' : 'text-[#9CA3AF] bg-[#F3F4F6] hover:bg-[#E5E7EB]'}`}>{m.featured ? '取消置顶' : '置顶'}</button>}
                    {canWrite && <button onClick={() => openEdit(m)} className="px-2 py-1 text-[10px] font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded cursor-pointer whitespace-nowrap transition-colors">编辑</button>}
                    {canDelete && <button onClick={() => setDeleteConfirm(m.id)} className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer whitespace-nowrap transition-colors">删除</button>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6] shrink-0">
              <h3 className="font-bold text-[#18181B]">{editing ? '编辑报道' : '添加报道'}</h3>
              <button onClick={() => setShowModal(false)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <form onSubmit={handleSave} className="overflow-y-auto flex-1 px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">媒体来源</label>
                  <input type="text" value={form.source || ''} onChange={(e) => f('source', e.target.value)} required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">来源标识颜色</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.sourceLogoColor || '#374151'} onChange={(e) => f('sourceLogoColor', e.target.value)}
                      className="w-9 h-9 rounded border border-[#E5E7EB] cursor-pointer p-0.5" />
                    <input type="text" value={form.sourceLogoColor || ''} onChange={(e) => f('sourceLogoColor', e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none font-mono" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">分类（中）</label>
                  <input type="text" value={form.category || ''} onChange={(e) => f('category', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">分类（英）</label>
                  <input type="text" value={form.categoryEn || ''} onChange={(e) => f('categoryEn', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">发布日期</label>
                  <input type="date" value={form.date || ''} onChange={(e) => f('date', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">原文链接</label>
                  <input type="text" value={form.url || ''} onChange={(e) => f('url', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none font-mono text-xs" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">标题（中）</label>
                  <input type="text" value={form.title || ''} onChange={(e) => f('title', e.target.value)} required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#F59E0B]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">标题（英）</label>
                  <input type="text" value={form.titleEn || ''} onChange={(e) => f('titleEn', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">摘要（中）</label>
                  <textarea value={form.excerpt || ''} onChange={(e) => f('excerpt', e.target.value)} rows={3}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none resize-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">摘要（英）</label>
                  <textarea value={form.excerptEn || ''} onChange={(e) => f('excerptEn', e.target.value)} rows={3}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none resize-none" />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.featured} onChange={(e) => f('featured', e.target.checked)} className="rounded" />
                    <span className="text-sm text-[#374151]">置顶显示（首页精选）</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 mt-5 pt-4 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] rounded-lg cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" className="flex-1 py-2.5 text-sm font-medium text-white bg-[#18181B] rounded-lg cursor-pointer whitespace-nowrap">{editing ? '保存修改' : '添加报道'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4 mx-auto"><i className="ri-delete-bin-line text-red-500 text-lg" /></div>
            <h3 className="font-bold text-[#18181B] text-center mb-2">确认删除</h3>
            <p className="text-sm text-[#6B7280] text-center mb-5">删除后无法恢复，确认删除吗？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] rounded-lg cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
